import React from 'react';

import type { CodeBlockRedlineRemove } from '@aline/common';
import { equals } from 'ramda';
import { useSuggestionGroupNavigationHooks } from 'src/editor/EditorAlert/SuggestionsAlert/useNavigationHooks';
import { HtmlPreview } from 'src/utils/HtmlPreview';
import { suggestHtmlPreviewStyles } from 'src/utils/preview-styles';
import { makeStyles } from 'tss-react/mui';

import {
  ExpandableRedlineCard,
  RedlinePreviewSection,
  RedlineReasoning,
} from './ExpandableRedlineCard';
import type { SuggestionStatus } from '../../../../chat-types';
import { createAIChatItem } from '../../../helpers/aiChatItem';

const useStyles = makeStyles()(theme => ({
  /**
   * The removed content as structured HTML — paragraphs, lists, and tables
   * keep their shape — styled as struck-through red text with the remove tint
   * behind each block, like the "delete" segments of the edit-paragraph cards.
   */
  htmlPreview: suggestHtmlPreviewStyles(theme, 'remove'),
}));

interface RedlineRemoveItemProps {
  messageId: string;
  remove: CodeBlockRedlineRemove;
  /**
   * The currently selected groupId for navigation highlighting. When it matches
   * the removal's groupId, the card is visually highlighted.
   */
  selectedGroupId: string | null;
  status: SuggestionStatus;
  threadId: string;
}

/**
 * Renders a single `redline-remove` (a paragraph range the AI suggested
 * deleting from the document) as an expandable card, mirroring
 * {@link RedlineInsertItem}:
 * - Header with the reasoning and status chip
 * - Expandable body with the full reasoning and a struck-through preview of
 *   the removed text
 */
export const RedlineRemoveItem = React.memo(
  function RedlineRemoveItem({
    messageId,
    remove,
    selectedGroupId,
    status,
    threadId,
  }: RedlineRemoveItemProps) {
    const { classes } = useStyles();
    const isSelected = selectedGroupId === remove.groupId;
    const { handleNavigateToRedlineAction } = useSuggestionGroupNavigationHooks();

    const elementId = createAIChatItem({
      groupId: remove.groupId,
      messageId,
      threadId,
      type: 'suggestion',
    });

    return (
      <ExpandableRedlineCard
        elementId={elementId}
        isSelected={isSelected}
        status={status}
        title={remove.reasoning}
        onCardClick={() =>
          handleNavigateToRedlineAction({
            documentId: remove.documentId,
            groupId: remove.groupId,
            locationAnchor: remove.locationAnchor,
          })
        }
      >
        <RedlineReasoning reasoning={remove.reasoning} />
        <RedlinePreviewSection title="Removed text:">
          <HtmlPreview className={classes.htmlPreview} html={remove.html} />
        </RedlinePreviewSection>
      </ExpandableRedlineCard>
    );
  },
  // Deep equality check to avoid re-renders during streaming
  (prev, next) => equals(prev, next)
);
